import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, NewspaperIcon, TrophyIcon, UsersIcon } from '@heroicons/react/24/outline';
import HeroSection from '../../components/common/HeroSection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const HomePage = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomePageData();
  }, []);

  const fetchHomePageData = async () => {
    try {
      setLoading(true);
      
      // Fetch latest news
      const newsResponse = await fetch(`${API_BASE_URL}/news?limit=3&published=true`);
      if (newsResponse.ok) {
        const newsData = await newsResponse.json();
        setLatestNews(newsData.articles || []);
      }
      
      // Fetch upcoming events
      const eventsResponse = await fetch(`${API_BASE_URL}/events/upcoming?limit=5`);
      if (eventsResponse.ok) {
        const eventsData = await eventsResponse.json();
        setUpcomingEvents(eventsData.events || []);
      }
    } catch (error) {
      console.error('Error fetching homepage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatEventTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection />



      {/* Latest News Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-primary mb-4">Latest News</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Stay updated with the latest happenings in East End Prep athletics
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="spinner spinner-primary w-8 h-8"></div>
            </div>
          ) : latestNews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {latestNews.map((article) => (
                <article key={article.id} className="card card-hover">
                  {(article.featuredImageSignedUrl || article.featuredImage) && (
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={article.featuredImageSignedUrl || article.featuredImage}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <span className="badge badge-primary mr-2">
                        {article.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(article.publishedAt)}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      <Link 
                        to={`/news/${article.slug}`}
                        className="hover:text-primary transition-colors duration-200"
                      >
                        {article.title}
                      </Link>
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    
                    <Link
                      to={`/news/${article.slug}`}
                      className="text-primary font-medium hover:text-secondary transition-colors duration-200"
                    >
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <NewspaperIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No news articles available at the moment.</p>
            </div>
          )}
          
          <div className="text-center">
            <Link
              to="/news"
              className="btn-primary inline-flex items-center"
            >
              View All News
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-primary mb-4">Upcoming Events</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't miss out on exciting games and events
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="spinner spinner-primary w-8 h-8"></div>
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="space-y-4 mb-8">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="card p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-primary rounded-lg flex flex-col items-center justify-center text-white">
                          <div className="text-xs font-medium">
                            {formatEventDate(event.startDate).split(' ')[0]}
                          </div>
                          <div className="text-lg font-bold">
                            {formatEventDate(event.startDate).split(' ')[2]}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {event.title}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            {formatEventTime(event.startDate)}
                          </div>
                          
                          {event.location && (
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              {event.location}
                            </div>
                          )}
                          
                          {event.sport && (
                            <span className="badge badge-primary">
                              {event.sport.name}
                            </span>
                          )}
                          
                          {event.eventType === 'game' && event.opponent && (
                            <span className="text-primary font-medium">
                              vs {event.opponent}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 md:mt-0 md:ml-4">
                      <Link
                        to={`/events/${event.id}`}
                        className="btn-outline text-sm px-4 py-2"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No upcoming events scheduled.</p>
            </div>
          )}
          
          <div className="text-center">
            <Link
              to="/events"
              className="btn-primary inline-flex items-center"
            >
              View All Events
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-md text-white mb-6">
            Ready to Join the Eagles?
          </h2>
          
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Discover your potential and become part of our championship tradition. 
            Excellence starts here.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sports"
              className="btn-secondary text-lg px-8 py-3 inline-flex items-center justify-center"
            >
              <UsersIcon className="w-5 h-5 mr-2" />
              Join a Team
            </Link>
            
            <Link
              to="/contact"
              className="btn-outline bg-white border-white text-secondary hover:bg-blue-50 text-lg px-8 py-3 inline-flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
      
      {/* Version Number */}
      <div className="fixed bottom-4 right-4 text-xs text-gray-400 font-mono">
        v1.0.0
      </div>
    </div>
  );
};

export default HomePage;